import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { PageHeader } from '../components/common';
import { ROUTES } from '../constants';
import { ADMIN_ROLE_CODES, ADMIN_ROLE_NAMES } from '../constants/userRoles';
import { profileService } from '../services';
import { useAuth } from '../contexts/AuthContext';

const roleBadgeStyles = {
  SA: {
    bg: 'bg-amber-50 text-amber-800 border-amber-200',
    icon: (
      <svg className='w-3.5 h-3.5 mr-1 text-amber-600' fill='currentColor' viewBox='0 0 20 20'>
        <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
      </svg>
    ),
  },
  A: {
    bg: 'bg-purple-50 text-purple-800 border-purple-200',
    icon: (
      <svg className='w-3.5 h-3.5 mr-1 text-purple-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
      </svg>
    ),
  },
  P: { bg: 'bg-blue-50 text-blue-800 border-blue-200', icon: null },
  M: { bg: 'bg-emerald-50 text-emerald-800 border-emerald-200', icon: null },
  S: { bg: 'bg-gray-50 text-gray-800 border-gray-200', icon: null },
};

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const location = useLocation();

  // Active tab: 'profile' | 'password' | 'account'
  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || (location.hash === '#password' ? 'password' : 'profile')
  );

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    } else if (location.hash === '#password') {
      setActiveTab('password');
    }
  }, [location.state, location.hash]);

  // Profile data & loading states
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [copiedId, setCopiedId] = useState(false);

  // Edit profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
  });
  const [submittingProfile, setSubmittingProfile] = useState(false);

  // Change password form state
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submittingPassword, setSubmittingPassword] = useState(false);

  // Fetch current user profile
  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await profileService.getProfile();
      if (response && response.data) {
        const data = response.data;
        setProfile(data);
        setProfileForm({
          name: data.name || '',
          phone: data.phone || '',
        });
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.message || 'Failed to load user profile.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Handle profile form submit
  const handleProfileSubmit = async e => {
    e.preventDefault();
    if (!profileForm.name.trim()) return;

    setSubmittingProfile(true);
    try {
      const res = await profileService.updateProfile({
        name: profileForm.name.trim(),
        phone: profileForm.phone.trim(),
      });

      const updated = res?.data || { ...profile, ...profileForm };
      setProfile(updated);

      // Update AuthContext so Header display name syncs immediately
      if (updateUser) {
        updateUser({
          name: updated.name,
          firstName: updated.name.split(' ')[0],
          lastName: updated.name.split(' ').slice(1).join(' '),
        });
      }

      setFeedback({
        type: 'success',
        message: 'Profile information updated and synchronized across accounts.',
      });
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.message || 'Failed to update profile.',
      });
    } finally {
      setSubmittingProfile(false);
    }
  };

  // Password strength calculation
  const getPasswordStrength = pwd => {
    if (!pwd) return { score: 0, text: 'Empty', color: 'bg-gray-200' };
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;

    if (score <= 1) return { score: 1, text: 'Weak', color: 'bg-red-500' };
    if (score === 2) return { score: 2, text: 'Fair', color: 'bg-amber-500' };
    if (score === 3 || score === 4) return { score: 3, text: 'Good', color: 'bg-blue-500' };
    return { score: 4, text: 'Strong', color: 'bg-emerald-500' };
  };

  const passwordStrength = getPasswordStrength(passwordForm.newPassword);

  // Handle password form submit
  const handlePasswordSubmit = async e => {
    e.preventDefault();
    if (!passwordForm.oldPassword || !passwordForm.newPassword) return;

    if (passwordForm.newPassword.length < 8) {
      setFeedback({
        type: 'error',
        message: 'New password must be at least 8 characters long.',
      });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setFeedback({
        type: 'error',
        message: 'New password and confirmation do not match.',
      });
      return;
    }

    if (passwordForm.oldPassword === passwordForm.newPassword) {
      setFeedback({
        type: 'error',
        message: 'New password cannot be the same as your current password.',
      });
      return;
    }

    setSubmittingPassword(true);
    try {
      await profileService.changePassword({
        oldPassword: passwordForm.oldPassword,
        password: passwordForm.newPassword,
      });

      setFeedback({
        type: 'success',
        message: 'Password successfully updated across all authentication systems.',
      });

      setPasswordForm({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.message || 'Failed to change password. Please check your current password.',
      });
    } finally {
      setSubmittingPassword(false);
    }
  };

  // Copy account ID to clipboard
  const handleCopyId = () => {
    if (!profile?.id) return;
    navigator.clipboard.writeText(profile.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  // Auto-dismiss feedback alert
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const initials = (profile?.name || user?.name || profile?.email || 'A')
    .split(' ')
    .map(p => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const roleCode = profile?.role || (user?.role === 'super_admin' ? 'SA' : 'A');
  const roleBadge = roleBadgeStyles[roleCode] || roleBadgeStyles.S;
  const roleName = profile?.roleName || ADMIN_ROLE_NAMES[roleCode] || roleCode;

  return (
    <div className='min-h-screen pb-12'>
      <PageHeader
        title='My Profile'
        subtitle='Manage your account settings, personal details, and security credentials'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Profile' },
        ]}
      />

      <div>
        {/* Feedback Alert Banner */}
        {feedback && (
          <div
            className={`mb-6 p-4 rounded-xl border flex items-center justify-between shadow-sm transition-all duration-300 ${
              feedback.type === 'success'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <div className='flex items-center space-x-3'>
              {feedback.type === 'success' ? (
                <svg className='w-5 h-5 text-emerald-600' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                </svg>
              ) : (
                <svg className='w-5 h-5 text-red-600' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z' clipRule='evenodd' />
                </svg>
              )}
              <span className='text-sm font-medium'>{feedback.message}</span>
            </div>
            <button
              type='button'
              onClick={() => setFeedback(null)}
              className='text-gray-400 hover:text-gray-600 p-1'
            >
              <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </button>
          </div>
        )}

        {loading ? (
          <div className='bg-white p-12 rounded-2xl border border-gray-200 shadow-sm text-center'>
            <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
            <p className='mt-3 text-sm text-gray-500'>Loading your profile details...</p>
          </div>
        ) : error ? (
          <div className='bg-white p-12 rounded-2xl border border-gray-200 shadow-sm text-center'>
            <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 text-red-600 mb-3'>
              <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' />
              </svg>
            </div>
            <h3 className='text-base font-semibold text-gray-900'>Error loading profile</h3>
            <p className='text-sm text-gray-500 mt-1'>{error}</p>
            <button
              type='button'
              onClick={fetchProfile}
              className='mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700'
            >
              Retry
            </button>
          </div>
        ) : (
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
            {/* Left Column: Profile Summary Card */}
            <div className='lg:col-span-1 space-y-6'>
              <div className='bg-white rounded-2xl border border-gray-200 shadow-sm p-6 text-center'>
                <div className='relative inline-block'>
                  <div className='w-24 h-24 rounded-2xl bg-gradient-to-tr from-blue-600 via-indigo-600 to-purple-600 flex items-center justify-center text-white text-2xl font-extrabold shadow-md mx-auto'>
                    {initials}
                  </div>
                  <span className='absolute bottom-0 right-0 block h-4 w-4 rounded-full bg-emerald-500 ring-2 ring-white'></span>
                </div>

                <h2 className='text-xl font-bold text-gray-900 mt-4'>{profile?.name || 'Administrator'}</h2>
                <p className='text-xs text-gray-500 mt-0.5'>{profile?.email}</p>

                <div className='mt-3 flex justify-center'>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border shadow-sm ${roleBadge.bg}`}>
                    {roleBadge.icon}
                    {roleName}
                  </span>
                </div>

                <div className='mt-6 pt-6 border-t border-gray-100 text-left space-y-3.5 text-xs'>
                  <div className='flex items-center justify-between'>
                    <span className='text-gray-500'>Account ID</span>
                    <button
                      type='button'
                      onClick={handleCopyId}
                      className='inline-flex items-center text-gray-700 hover:text-blue-600 font-mono bg-gray-50 px-2 py-1 rounded border border-gray-200'
                      title='Copy Account ID'
                    >
                      <span>{profile?.id?.slice(0, 8)}...</span>
                      <svg className='w-3.5 h-3.5 ml-1 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2z' />
                      </svg>
                    </button>
                  </div>
                  {copiedId && (
                    <div className='text-right text-[10px] text-emerald-600 font-semibold'>Copied to clipboard!</div>
                  )}

                  <div className='flex items-center justify-between'>
                    <span className='text-gray-500'>Account Status</span>
                    <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-800'>
                      Active
                    </span>
                  </div>

                  <div className='flex items-center justify-between'>
                    <span className='text-gray-500'>Member Since</span>
                    <span className='text-gray-800 font-medium'>
                      {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : '—'}
                    </span>
                  </div>

                  <div className='flex items-center justify-between'>
                    <span className='text-gray-500'>Access Level</span>
                    <span className='text-gray-800 font-medium'>
                      {roleCode === 'SA' ? 'Full CRUD Privileges' : 'Read-Only Portal Access'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Navigation Tabs Pill List (Quick Navigation) */}
              <div className='bg-white rounded-2xl border border-gray-200 shadow-sm p-3'>
                <div className='space-y-1'>
                  <button
                    type='button'
                    onClick={() => setActiveTab('profile')}
                    className={`w-full text-left px-3.5 py-2.5 rounded-xl text-sm font-medium transition flex items-center justify-between ${
                      activeTab === 'profile'
                        ? 'bg-blue-50 text-blue-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className='flex items-center'>
                      <svg className='w-4 h-4 mr-2.5 text-gray-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' />
                      </svg>
                      Profile Details
                    </span>
                    <svg className='w-4 h-4 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                    </svg>
                  </button>

                  <button
                    type='button'
                    onClick={() => setActiveTab('password')}
                    className={`w-full text-left px-3.5 py-2.5 rounded-xl text-sm font-medium transition flex items-center justify-between ${
                      activeTab === 'password'
                        ? 'bg-blue-50 text-blue-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className='flex items-center'>
                      <svg className='w-4 h-4 mr-2.5 text-gray-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' />
                      </svg>
                      Security & Password
                    </span>
                    <svg className='w-4 h-4 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                    </svg>
                  </button>

                  <button
                    type='button'
                    onClick={() => setActiveTab('account')}
                    className={`w-full text-left px-3.5 py-2.5 rounded-xl text-sm font-medium transition flex items-center justify-between ${
                      activeTab === 'account'
                        ? 'bg-blue-50 text-blue-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className='flex items-center'>
                      <svg className='w-4 h-4 mr-2.5 text-gray-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                      </svg>
                      Account Overview
                    </span>
                    <svg className='w-4 h-4 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column: Interactive Panes */}
            <div className='lg:col-span-2'>
              {/* Tab 1: Profile Details Form */}
              {activeTab === 'profile' && (
                <div className='bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden'>
                  <div className='px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between'>
                    <div>
                      <h3 className='text-base font-bold text-gray-900'>Personal Profile</h3>
                      <p className='text-xs text-gray-500 mt-0.5'>Update your full name and contact information</p>
                    </div>
                  </div>

                  <form onSubmit={handleProfileSubmit} className='p-6 space-y-5'>
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5'>
                        Full Name <span className='text-red-500'>*</span>
                      </label>
                      <input
                        type='text'
                        required
                        value={profileForm.name}
                        onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                        placeholder='Enter your full name'
                        className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                      />
                    </div>

                    <div>
                      <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5'>
                        Phone Number
                      </label>
                      <input
                        type='tel'
                        value={profileForm.phone}
                        onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                        placeholder='+39 333 1234567'
                        className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                      />
                    </div>

                    <div>
                      <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5'>
                        Email Address
                      </label>
                      <div className='relative'>
                        <input
                          type='email'
                          disabled
                          value={profile?.email || ''}
                          className='w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed'
                        />
                        <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'>
                          <svg className='h-4 w-4 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' />
                          </svg>
                        </div>
                      </div>
                      <p className='text-[11px] text-gray-400 mt-1'>
                        Your email is permanently linked to your authentication login identity.
                      </p>
                    </div>

                    <div>
                      <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5'>
                        Assigned Role
                      </label>
                      <input
                        type='text'
                        disabled
                        value={roleName}
                        className='w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600 cursor-not-allowed font-medium'
                      />
                    </div>

                    {/* Dual-Sync notice */}
                    <div className='p-3.5 rounded-xl bg-blue-50 border border-blue-200 text-xs text-blue-900 flex items-start space-x-2.5'>
                      <svg className='w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
                      </svg>
                      <div>
                        <strong>Automatic Synchronization:</strong> Updating your personal details will atomically persist and synchronize across both <code>skaix_account</code> and <code>account</code> tables.
                      </div>
                    </div>

                    <div className='pt-4 border-t border-gray-200 flex justify-end'>
                      <button
                        type='submit'
                        disabled={submittingProfile || !profileForm.name.trim()}
                        className='px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm transition duration-150'
                      >
                        {submittingProfile && (
                          <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                        )}
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Tab 2: Security & Change Password */}
              {activeTab === 'password' && (
                <div className='bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden'>
                  <div className='px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between'>
                    <div>
                      <h3 className='text-base font-bold text-gray-900'>Change Password</h3>
                      <p className='text-xs text-gray-500 mt-0.5'>Update your account password securely across both tables</p>
                    </div>
                  </div>

                  <form onSubmit={handlePasswordSubmit} className='p-6 space-y-5'>
                    {/* Current Password */}
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5'>
                        Current Password <span className='text-red-500'>*</span>
                      </label>
                      <div className='relative'>
                        <input
                          type={showOldPassword ? 'text' : 'password'}
                          required
                          value={passwordForm.oldPassword}
                          onChange={e => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                          placeholder='Enter current password'
                          className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 pr-10'
                        />
                        <button
                          type='button'
                          onClick={() => setShowOldPassword(!showOldPassword)}
                          className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600'
                        >
                          {showOldPassword ? (
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18' />
                            </svg>
                          ) : (
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* New Password */}
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5'>
                        New Password <span className='text-red-500'>*</span>
                      </label>
                      <div className='relative'>
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          required
                          value={passwordForm.newPassword}
                          onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                          placeholder='Minimum 8 characters'
                          className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 pr-10'
                        />
                        <button
                          type='button'
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600'
                        >
                          {showNewPassword ? (
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18' />
                            </svg>
                          ) : (
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
                            </svg>
                          )}
                        </button>
                      </div>

                      {/* Password Strength Indicator */}
                      {passwordForm.newPassword && (
                        <div className='mt-2'>
                          <div className='flex items-center justify-between text-[11px] mb-1'>
                            <span className='text-gray-500'>Password Strength:</span>
                            <span className='font-semibold text-gray-700'>{passwordStrength.text}</span>
                          </div>
                          <div className='h-1.5 w-full bg-gray-100 rounded-full overflow-hidden'>
                            <div
                              className={`h-full ${passwordStrength.color} transition-all duration-300`}
                              style={{ width: `${(passwordStrength.score / 4) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Confirm New Password */}
                    <div>
                      <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5'>
                        Confirm New Password <span className='text-red-500'>*</span>
                      </label>
                      <div className='relative'>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          required
                          value={passwordForm.confirmPassword}
                          onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                          placeholder='Re-type new password'
                          className='w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 pr-10'
                        />
                        <button
                          type='button'
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600'
                        >
                          {showConfirmPassword ? (
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18' />
                            </svg>
                          ) : (
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
                            </svg>
                          )}
                        </button>
                      </div>

                      {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                        <p className='text-xs text-red-500 mt-1 flex items-center'>
                          <svg className='w-3.5 h-3.5 mr-1' fill='currentColor' viewBox='0 0 20 20'>
                            <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z' clipRule='evenodd' />
                          </svg>
                          Passwords do not match
                        </p>
                      )}
                    </div>

                    {/* Criteria Box */}
                    <div className='p-3.5 rounded-xl bg-gray-50 border border-gray-200 text-xs text-gray-600 space-y-1.5'>
                      <div className='font-semibold text-gray-700'>Password Guidelines:</div>
                      <div className='flex items-center space-x-2'>
                        <span className={`w-1.5 h-1.5 rounded-full ${passwordForm.newPassword.length >= 8 ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                        <span>At least 8 characters long</span>
                      </div>
                      <div className='flex items-center space-x-2'>
                        <span className={`w-1.5 h-1.5 rounded-full ${passwordForm.newPassword && passwordForm.newPassword !== passwordForm.oldPassword ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                        <span>Different from your current password</span>
                      </div>
                    </div>

                    <div className='pt-4 border-t border-gray-200 flex justify-end'>
                      <button
                        type='submit'
                        disabled={
                          submittingPassword ||
                          !passwordForm.oldPassword ||
                          !passwordForm.newPassword ||
                          passwordForm.newPassword !== passwordForm.confirmPassword
                        }
                        className='px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm transition duration-150'
                      >
                        {submittingPassword && (
                          <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                        )}
                        Update Password
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Tab 3: Account Overview */}
              {activeTab === 'account' && (
                <div className='bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden'>
                  <div className='px-6 py-4 border-b border-gray-200 bg-gray-50'>
                    <h3 className='text-base font-bold text-gray-900'>Account & Security Overview</h3>
                    <p className='text-xs text-gray-500 mt-0.5'>Technical specifications and role permissions</p>
                  </div>

                  <div className='p-6 space-y-6'>
                    <div>
                      <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3'>
                        Identity & Credentials
                      </h4>
                      <dl className='grid grid-cols-1 md:grid-cols-2 gap-4 text-xs'>
                        <div className='p-3.5 rounded-xl border border-gray-200 bg-gray-50'>
                          <dt className='text-gray-500 font-medium'>Account Identifier (UUID)</dt>
                          <dd className='text-gray-900 font-mono mt-1 break-all'>{profile?.id}</dd>
                        </div>
                        <div className='p-3.5 rounded-xl border border-gray-200 bg-gray-50'>
                          <dt className='text-gray-500 font-medium'>Role Code</dt>
                          <dd className='text-gray-900 font-semibold mt-1'>{roleCode} ({roleName})</dd>
                        </div>
                        <div className='p-3.5 rounded-xl border border-gray-200 bg-gray-50'>
                          <dt className='text-gray-500 font-medium'>Data Encryption</dt>
                          <dd className='text-emerald-700 font-semibold mt-1'>AES-256 (Email & Phone)</dd>
                        </div>
                        <div className='p-3.5 rounded-xl border border-gray-200 bg-gray-50'>
                          <dt className='text-gray-500 font-medium'>Password Hashing</dt>
                          <dd className='text-emerald-700 font-semibold mt-1'>Secure Argon2/Bcrypt</dd>
                        </div>
                      </dl>
                    </div>

                    <div className='pt-6 border-t border-gray-200'>
                      <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3'>
                        Role Privileges Breakdown
                      </h4>
                      <div className='space-y-2 text-xs'>
                        {roleCode === 'SA' ? (
                          <>
                            <div className='flex items-center text-emerald-700'>
                              <svg className='w-4 h-4 mr-2 text-emerald-500' fill='currentColor' viewBox='0 0 20 20'>
                                <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                              </svg>
                              Full Administrative CRUD Control
                            </div>
                            <div className='flex items-center text-emerald-700'>
                              <svg className='w-4 h-4 mr-2 text-emerald-500' fill='currentColor' viewBox='0 0 20 20'>
                                <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                              </svg>
                              Invite and create new administrators with welcome dispatch
                            </div>
                            <div className='flex items-center text-emerald-700'>
                              <svg className='w-4 h-4 mr-2 text-emerald-500' fill='currentColor' viewBox='0 0 20 20'>
                                <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                              </svg>
                              Update, activate, deactivate, and revoke admin user access
                            </div>
                          </>
                        ) : (
                          <>
                            <div className='flex items-center text-blue-700'>
                              <svg className='w-4 h-4 mr-2 text-blue-500' fill='currentColor' viewBox='0 0 20 20'>
                                <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                              </svg>
                              Read-Only Access to Admin Portal Directory
                            </div>
                            <div className='flex items-center text-gray-500'>
                              <svg className='w-4 h-4 mr-2 text-gray-400' fill='currentColor' viewBox='0 0 20 20'>
                                <path fillRule='evenodd' d='M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z' clipRule='evenodd' />
                              </svg>
                              User mutations, invitations, and role editing restricted to Super Admins
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
